import { Button, Group, Select, Stack, TextInput, RangeSlider, RangeSliderValue, NumberInput, Text } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { useDebouncedCallback } from '@mantine/hooks';
import { FC, useEffect, useState } from 'react';

import { Filter, FiltersTypes, ContainsFilter, DateRangeFilter, NumberRangeFilter, RangeSliderFilter, SelectFilter as SelectFilterType } from '../../data-access/types/filters';

import { useColFilter, useColFilters } from '@data-access';

type FiltersProps = {
  filters: Record<string, Filter>;
};

const SelectFilter: FC<SelectFilterType & { name: string }> = ({
  name,
  values,
  label,
}) => {
  const [value, setFilter] = useColFilter('samplingPlansMap', name);

  return (
    <Select
      comboboxProps={{ withinPortal: false }}
      data={values?.map((v) => ({ value: v.id.toString(), label: v.name }))}
      label={label}
      onChange={(v) => setFilter(v)}
      value={(value as string | null | undefined) || null}
    />
  );
};

const ContainsFilterComponent: FC<{ filter: ContainsFilter; name: string }> = ({
  filter,
  name,
}) => {
  const [filterValue, setFilter] = useColFilter('samplingPlansMap', name);
  const [value, setValue] = useState(filterValue || '');

  const handleSearch = useDebouncedCallback((query: string) => {
    setFilter(query);
  }, 300);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.currentTarget.value);
    handleSearch(event.currentTarget.value);
  };

  useEffect(() => {
    if (!filterValue) {
      setValue('');
    }
  }, [filterValue]);

  return (
    <TextInput
      label={filter.label}
      onChange={handleChange}
      placeholder={filter.label}
      value={value as string | undefined}
    />
  );
};

const DateRangeFilterComponent: FC<{ filter: DateRangeFilter; name: string }> = ({
  filter,
  name,
}) => {
  const [filterValue, setFilter] = useColFilter<DatesRangeValue>('samplingPlansMap', name);

  const handleChange = (value: DatesRangeValue) => {
    setFilter(value);
  };

  return (
    <DatePickerInput
      label={filter.label}
      onChange={handleChange}
      placeholder={filter.label}
      popoverProps={{ withinPortal: false }}
      type="range"
      value={(filterValue as DatesRangeValue) || [null, null]}
    />
  );
};

const NumberRangeFilterComponent: FC<{ filter: NumberRangeFilter; name: string }> = ({
  filter,
  name,
}) => {
  const [filterValue, setFilter] = useColFilter('samplingPlansMap', name);
  const [range, setRange] = useState(
    (filterValue as [number | null, number | null]) || [null, null],
  );

  const handleMinMaxChange = useDebouncedCallback(
    (min: number | null, max: number | null) => {
      setFilter([min, max]);
    },
    300,
  );

  const handleMinChange = (value: string | number) => {
    setRange([value === '' ? null : Number(value), range[1]]);
    handleMinMaxChange(value === '' ? null : Number(value), range[1]);
  };

  const handleMaxChange = (value: string | number) => {
    setRange([range[0], value === '' ? null : Number(value)]);
    handleMinMaxChange(range[0], value === '' ? null : Number(value));
  };

  useEffect(() => {
    if (!filterValue) {
      setRange([null, null]);
    }
  }, [filterValue]);

  return (
    <div style={{ display: 'flex', gap: '1rem' }}>
      <NumberInput
        label={filter.label}
        max={filter.range?.[1] ?? undefined}
        min={filter.range?.[0] ?? undefined}
        onChange={handleMinChange}
        placeholder="Min"
        value={range[0] === null ? '' : range[0]}
      />
      <NumberInput
        label=" "
        max={filter.range?.[1] ?? undefined}
        min={filter.range?.[0] ?? undefined}
        onChange={handleMaxChange}
        placeholder="Max"
        value={range[1] === null ? '' : range[1]}
      />
    </div>
  );
};

const RangeSliderFilterComponent: FC<{ filter: RangeSliderFilter; name: string }> = ({
  filter,
  name,
}) => {
  const [filterValue, setFilter] = useColFilter('samplingPlansMap', name);
  const [range, setRange] = useState(
    (filterValue as RangeSliderValue) || [null, null],
  );

  const handleRangeChange = useDebouncedCallback((value: RangeSliderValue) => {
    setFilter(value);
  }, 300);

  const handleChange = (value: RangeSliderValue) => {
    setRange(value);
    handleRangeChange(value);
  };

  return (
    <div>
      <Text>{filter.label}</Text>
      <RangeSlider
        label={(value) => value}
        max={filter.range?.[1] ?? 100}
        min={filter.range?.[0] ?? 0}
        onChange={handleChange}
        value={range}
      />
    </div>
  );
};

export const SamplingPlansFiltersMenu: FC<FiltersProps> = ({ filters }) => {
  const [, , resetFilters] = useColFilters('samplingPlansMap');
  return (
    <Stack>
      {Object.entries(filters).map(([name, filter]) => {
        switch (filter.filter_type) {
          case FiltersTypes.Select:
            return <SelectFilter key={name} {...filter} name={name} />;
          case FiltersTypes.Contains:
            return (
              <ContainsFilterComponent filter={filter} key={name} name={name} />
            );
          case FiltersTypes.DateRange:
            return (
              <DateRangeFilterComponent
                filter={filter}
                key={name}
                name={name}
              />
            );
          case FiltersTypes.NumberRange:
            return (
              <NumberRangeFilterComponent
                filter={filter}
                key={name}
                name={name}
              />
            );
          case FiltersTypes.RangeSlider:
            return (
              <RangeSliderFilterComponent
                filter={filter}
                key={name}
                name={name}
              />
            );
          default:
            return null;
        }
      })}
      <Group justify="start">
        <Button color="red" onClick={() => resetFilters()} variant="light">
          Reset Filters
        </Button>
      </Group>
    </Stack>
  );
};
